import React, { useCallback, useEffect, useState } from "react";
import {
	getPhotos,
	getSelfie,
	getUsersModDetails,
} from "../api/apiServiceFetch";
import { utcToLocalDateTime, utcToDate } from "./helper";
import { useTranslation } from "../contexts/TranslationContext";
import Popup from "./Popup";
import OrderPopup from "./OrderPopup";
import PhotoModal from "./PhotoModal";
import { getFileBaseUrl } from "../config/environment";
import userPng from "../pages/Reports/user.png";
import "../pages/SupportMessages/SupportMessages.scss";

const getAvatarSrc = (user) =>
	user?.mainPhoto
		? `${getFileBaseUrl()}/Photo/${user.userId}/${user.mainPhoto}`
		: userPng;

const handleAvatarError = (event) => {
	if (event.currentTarget.src !== userPng) {
		event.currentTarget.src = userPng;
	}
};

// Shared user-details card popup used by both the Support Chat page and the User
// Chats moderation page. Opened for any `user` (id + display + basic fields);
// loads details/photos/selfie and hosts the edit/delete Popup, purchases and
// photo viewer. Extracted so both pages show an identical card.
const ModerationUserCard = ({
	user,
	isOpen,
	onClose,
	onUserDeleted,
	onUserUpdated,
}) => {
	const { t } = useTranslation();
	const [cardLoading, setCardLoading] = useState(false);
	const [cardError, setCardError] = useState("");
	const [cardDetails, setCardDetails] = useState(null);
	const [cardPhotos, setCardPhotos] = useState([]);
	const [cardSelfie, setCardSelfie] = useState(null);
	const [isEditOpen, setIsEditOpen] = useState(false);
	const [isOrdersOpen, setIsOrdersOpen] = useState(false);
	const [viewPhoto, setViewPhoto] = useState(null);

	const userId = user?.userId;
	const display = user?.display || user?.sender || (userId ? `User #${userId}` : "");

	const fetchCardData = useCallback(async (id) => {
		setCardLoading(true);
		setCardError("");
		try {
			const [details, photos, selfie] = await Promise.allSettled([
				getUsersModDetails(id),
				getPhotos(id),
				getSelfie(id),
			]);

			setCardDetails(details.status === "fulfilled" ? details.value : null);
			setCardPhotos(
				photos.status === "fulfilled" && Array.isArray(photos.value)
					? photos.value
					: [],
			);
			setCardSelfie(selfie.status === "fulfilled" ? selfie.value : null);

			if (details.status !== "fulfilled") {
				setCardError("Не удалось загрузить данные пользователя.");
			}
		} finally {
			setCardLoading(false);
		}
	}, []);

	useEffect(() => {
		if (isOpen && userId) {
			setCardDetails(null);
			setCardPhotos([]);
			setCardSelfie(null);
			setIsEditOpen(false);
			setIsOrdersOpen(false);
			setViewPhoto(null);
			fetchCardData(userId);
		}
	}, [isOpen, userId, fetchCardData]);

	const handleEditClose = useCallback(() => {
		setIsEditOpen(false);
		if (userId) fetchCardData(userId);
		if (onUserUpdated) onUserUpdated();
	}, [fetchCardData, onUserUpdated, userId]);

	const handleUserDeleted = useCallback(() => {
		setIsEditOpen(false);
		if (onClose) onClose();
		if (onUserDeleted) onUserDeleted();
	}, [onClose, onUserDeleted]);

	if (!isOpen || !user) return null;

	const editData = {
		id: userId,
		userId: user.legacyUserId ?? 0,
		name: user.name ?? "",
		sName: user.sName ?? "",
		userName: user.userName ?? "",
		sex: user.sex ?? 1,
		birthday: user.birthday ?? "",
	};

	return (
		<>
			{viewPhoto && (
				<PhotoModal photo={viewPhoto} onClose={() => setViewPhoto(null)} />
			)}

			<Popup
				isOpen={isEditOpen}
				onClose={handleEditClose}
				editData={editData}
				userDetails={cardDetails}
				onConfirm={handleUserDeleted}
			/>

			<OrderPopup
				userId={userId}
				visible={isOrdersOpen}
				onClose={() => setIsOrdersOpen(false)}
			/>

			<div className="support-card-overlay">
				<div className="support-card">
					<button
						type="button"
						className="support-card__close"
						onClick={onClose}
						title="Закрыть"
					>
						<svg
							width="14"
							height="14"
							viewBox="0 0 14 14"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M1 1L13 13M13 1L1 13"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
							/>
						</svg>
					</button>

					<div className="support-card__header">
						<img
							className="support-card__avatar"
							src={getAvatarSrc(user)}
							onError={handleAvatarError}
							onClick={() => {
								if (user.mainPhoto) {
									setViewPhoto(getAvatarSrc(user));
								}
							}}
							alt="avatar"
						/>
						<div>
							<h3>{display}</h3>
							<p>
								id: {userId}
								{user.legacyUserId ? `, tg: ${user.legacyUserId}` : ""}
							</p>
						</div>
					</div>

					<div className="support-card__actions">
						<button
							type="button"
							className="support-card__action support-card__action--edit"
							onClick={() => setIsEditOpen(true)}
						>
							Редактировать
						</button>
						<button
							type="button"
							className="support-card__action support-card__action--orders"
							onClick={() => setIsOrdersOpen(true)}
						>
							Покупки
						</button>
					</div>

					{cardLoading && (
						<div className="support-messages__status">Загрузка...</div>
					)}
					{cardError && !cardLoading && (
						<div className="support-messages__status support-messages__status--error">
							{cardError}
						</div>
					)}

					{!cardLoading && (
						<>
							<div className="support-card__info">
								<p>Никнейм: {user.userName || "-"}</p>
								<p>Пол: {user.sex === 2 ? "Женщина" : "Мужчина"}</p>
								<p>
									Дата рождения:{" "}
									{user.birthday ? utcToDate(user.birthday) : "-"}
								</p>
								<p>Страна: {cardDetails?.country || "-"}</p>
								<p>Город: {cardDetails?.city || "-"}</p>
								<p>Почта: {cardDetails?.mail || "-"}</p>
								<p>OTP: {cardDetails?.otp || "-"}</p>
								<p>OTP Подтверждён: {cardDetails?.otpConfirmed ? "Да" : "Нет"}</p>
								{cardDetails?.wRole && <p>Роль: {t(cardDetails.wRole)}</p>}
								{cardDetails?.profession && (
									<p>Деятельность: {t(cardDetails.profession)}</p>
								)}
								{cardDetails?.description && (
									<p>О себе: {cardDetails.description}</p>
								)}
								{cardDetails?.propose && (
									<p>Цель знакомства: {t(cardDetails.propose)}</p>
								)}
								{(cardDetails?.minAge || cardDetails?.maxAge) && (
									<p>
										Возраст поиска: {cardDetails?.minAge ?? "-"} -{" "}
										{cardDetails?.maxAge ?? "-"}
									</p>
								)}
								{cardDetails?.height ? <p>Рост: {cardDetails.height}</p> : null}
								{cardDetails?.lang && <p>Язык: {cardDetails.lang}</p>}
								<p>Дата регистрации: {utcToLocalDateTime(cardDetails?.cDate)}</p>
								{cardDetails?.endDate && (
									<p>Подписка до: {utcToLocalDateTime(cardDetails.endDate)}</p>
								)}
								{cardDetails?.superLikes ? (
									<p>Суперлайки: {cardDetails.superLikes}</p>
								) : null}
								{(cardDetails?.cntReg > 1 || cardDetails?.cntDel > 0) && (
									<p>
										{cardDetails?.cntReg > 1 &&
											`Кол. регистраций: ${cardDetails.cntReg} `}
										{cardDetails?.cntDel > 0 &&
											`Удалений: ${cardDetails.cntDel}`}
									</p>
								)}
							</div>

							<div className="support-card__photos">
								{cardSelfie?.fileName ? (
									<div className="support-card__photo support-card__photo--selfie">
										<img
											src={`${getFileBaseUrl()}/Selfie/${userId}/${cardSelfie.fileName}`}
											onClick={() =>
												setViewPhoto(
													`${getFileBaseUrl()}/Selfie/${userId}/${cardSelfie.fileName}`,
												)
											}
											alt="Selfie"
										/>
										<span className="support-card__photo-label">Селфи</span>
									</div>
								) : (
									<div className="support-card__no-media">Нет селфи</div>
								)}

								{cardPhotos.length > 0 ? (
									cardPhotos.map((photo, index) => {
										const pct =
											typeof photo.matchPercent === "number"
												? photo.matchPercent
												: null;
										const tierColor =
											pct == null
												? "#777"
												: pct >= 50
													? "#2e7d32"
													: pct >= 30
														? "#ed6c02"
														: "#c62828";
										return (
											<div key={photo.id} className="support-card__photo">
												<img
													src={`${getFileBaseUrl()}/Photo/${userId}/${photo.fileName}`}
													onClick={() =>
														setViewPhoto(
															`${getFileBaseUrl()}/Photo/${userId}/${photo.fileName}`,
														)
													}
													alt={`Photo ${index + 1}`}
												/>
												<span
													className="support-card__badge"
													style={{ background: tierColor }}
													title={
														pct == null
															? "Лицо не сверено"
															: `Совпадение с селфи: ${pct.toFixed(0)}%`
													}
												>
													{pct == null ? "?" : `${pct.toFixed(0)}%`}
												</span>
												{photo.isMain && (
													<span className="support-card__photo-label">
														Главное
													</span>
												)}
											</div>
										);
									})
								) : (
									<div className="support-card__no-media">Нет фоток</div>
								)}
							</div>
						</>
					)}
				</div>
			</div>
		</>
	);
};

export default ModerationUserCard;
